import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getEventById } from "../../../../Services/Event";
import { getRegistrationById } from "../../../../Services/Registration";
import {
  buildRepresentativePayment,
  getAllPaymentsByRegistrationId,
} from "../../../../Services/Payment";
import { PaymentStatus } from "../../../../Enums/RegistrationEnum";
import { resolveBillingFirmForRegistration } from "../../../../utils/resolveBillingFirmForRegistration";
import { applyPrepaidFlagFromLedger } from "../utils/participantPaymentRules";
import { useDynamicPageMeta } from "../../../../hooks/useDynamicPageMeta";
import { timestampToDateString } from "../../../../utils/dates";
import { createRegistrationDraft } from "../constants";
import type { AppDispatch } from "../../../../redux/store";
import {
  setActiveTab as setActiveTabAction,
  setEventData,
  setFirmData,
  setHasSavedOnce,
  setPaymentData,
  setPayments,
  setRegistrationData,
  setTabs,
} from "../../../../redux/states/editParticipant.state";
import { useEditParticipantPatch } from "./useEditParticipantPatch";
import {
  selectCanOpenInvoiceTab,
  selectEditParticipantMeta,
  selectEventData,
  selectEventId,
  selectRegistrationData,
  selectRegistrationId,
  selectVisibleTabs,
} from "../editParticipant.selectors";

export function useEditParticipantData () {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const meta = useSelector(selectEditParticipantMeta);
  const eventId = useSelector(selectEventId);
  const registrationId = useSelector(selectRegistrationId);
  const eventData = useSelector(selectEventData);
  const registrationData = useSelector(selectRegistrationData);
  const visibleTabs = useSelector(selectVisibleTabs);
  const canOpenInvoiceTab = useSelector(selectCanOpenInvoiceTab);

  const { isEditMode, activeTab, hasSavedOnce } = meta;

  const setTabNames = useCallback(
    (paymentStatus: PaymentStatus) => {
      let secondTab = "SEND EMAIL INVOICE";
      if (paymentStatus === "completed") {
        secondTab = "SEND EMAIL PAYMENT";
      }
      dispatch(setTabs(["REGISTRATION", secondTab]));
    },
    [dispatch],
  );

  const fetchInvoiceData = useCallback(async () => {
    if (!eventId || !registrationId) {
      return;
    }
    try {
      const [event, registration, payments] = await Promise.all([
        getEventById(eventId),
        getRegistrationById(registrationId),
        getAllPaymentsByRegistrationId(registrationId),
      ]);
      dispatch(setEventData(event || undefined));
      dispatch(
        setRegistrationData(
          registration
            ? applyPrepaidFlagFromLedger(registration, payments)
            : registration,
        ),
      );
      dispatch(setPayments(payments));
      dispatch(setPaymentData(buildRepresentativePayment(payments) || undefined));
      dispatch(setFirmData(await resolveBillingFirmForRegistration(registration)));
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  }, [dispatch, eventId, registrationId]);

  const fetchEventData = useCallback(async () => {
    if (!eventId) {
      return;
    }
    try {
      const event = await getEventById(eventId);
      dispatch(setEventData(event || undefined));
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  }, [dispatch, eventId]);

  const { patchRegistration } = useEditParticipantPatch();

  useEffect(() => {
    if (registrationData?.paymentStatus) {
      setTabNames(registrationData.paymentStatus);
    }
  }, [registrationData?.paymentStatus, setTabNames]);

  useEffect(() => {
    if (isEditMode) {
      void fetchInvoiceData();
    } else {
      void fetchEventData();
    }
  }, [isEditMode, fetchInvoiceData, fetchEventData]);

  useEffect(() => {
    if (isEditMode || !eventId) {
      return;
    }
    if (registrationData?.id) {
      return;
    }

    if (!registrationData) {
      if (!eventData) {
        return;
      }
      dispatch(setRegistrationData(createRegistrationDraft(eventId, eventData)));
      return;
    }

    const nextEventName = eventData?.title ?? registrationData.eventName;
    const nextEventDate = eventData?.startDate ?? registrationData.eventDate;
    const nextEventType = eventData?.eventType ?? registrationData.eventType;

    if (
      registrationData.eventName === nextEventName &&
      registrationData.eventDate === nextEventDate &&
      registrationData.eventType === nextEventType
    ) {
      return;
    }

    dispatch(
      setRegistrationData({
        ...registrationData,
        eventName: nextEventName,
        eventDate: nextEventDate,
        eventType: nextEventType,
      }),
    );
  }, [
    dispatch,
    eventData,
    eventId,
    isEditMode,
    registrationData,
    registrationData?.eventDate,
    registrationData?.eventName,
    registrationData?.eventType,
    registrationData?.id,
  ]);

  useEffect(() => {
    if (!isEditMode && hasSavedOnce && registrationData) {
      dispatch(setTabs(["REGISTRATION", "SEND EMAIL INVOICE"]));
    }
  }, [dispatch, isEditMode, hasSavedOnce, registrationData]);

  const eventSubtitle = eventData?.startDate
    ? timestampToDateString(eventData.startDate)
    : null;

  useEffect(() => {
    if (!eventId) {
      return;
    }

    setPageMeta({
      title: eventData?.title || "Loading",
      subtitle: eventSubtitle || "",
      breadcrumbLabels: {
        [`/events-staff/${eventId}`]: eventData?.title || "Loading",
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [clearPageMeta, eventData?.title, eventId, eventSubtitle, setPageMeta]);

  const handleCancel = useCallback(() => {
    if (eventId) {
      navigate(`/events-staff/${eventId}/attendees`);
      return;
    }
    navigate(-1);
  }, [eventId, navigate]);

  const setActiveTab = useCallback(
    (tab: number) => dispatch(setActiveTabAction(tab)),
    [dispatch],
  );

  return {
    meta,
    eventData,
    registrationData,
    visibleTabs,
    canOpenInvoiceTab,
    activeTab,
    isEditMode,
    eventId,
    registrationId,
    patchRegistration,
    handleCancel,
    setActiveTab,
    setHasSavedOnce: (value: boolean) => dispatch(setHasSavedOnce(value)),
    fetchInvoiceData,
  };
}
